import axios from 'axios';

// Interfețe pentru răspunsurile de la Google Books API
interface GoogleBooksResponse {
  totalItems: number;
  items?: GoogleBooksItem[];
}

interface GoogleBooksItem {
  volumeInfo: {
    title: string;
    authors?: string[];
    description?: string;
    publishedDate?: string;
    categories?: string[];
    industryIdentifiers?: {
      type: string;
      identifier: string;
    }[];
    imageLinks?: {
      thumbnail?: string;
    };
    publisher?: string;
    language?: string;
    pageCount?: number;
  };
}

// Interfața pentru datele despre cărți returnate din API-uri externe
export interface BookMetadata {
  title?: string;
  author?: string;
  description?: string;
  publishedYear?: string;
  category?: string;
  isbn?: string;
  coverImageUrl?: string;
  publisher?: string;
  language?: string;
  pageCount?: number;
}

/**
 * Serviciu pentru preluarea metadatelor cărților din API-uri externe
 */
export const BookMetadataService = {
  /**
   * Caută o carte după ISBN folosind Google Books API
   * @param isbn ISBN-ul cărții (ISBN-10 sau ISBN-13)
   */
  searchByISBN: async (isbn: string): Promise<BookMetadata | null> => {
    try {
      if (!isbn || isbn.trim() === '') {
        throw new Error('ISBN-ul nu poate fi gol');
      }
      
      // Curățăm ISBN-ul de orice caracterele non-alfanumerice
      const cleanedISBN = isbn.replace(/[^0-9X]/gi, '');
      
      // Validăm că avem un ISBN valid (ISBN-10 are 10 caractere, ISBN-13 are 13)
      if (cleanedISBN.length !== 10 && cleanedISBN.length !== 13) {
        throw new Error('ISBN invalid. Trebuie să aibă 10 sau 13 caractere.');
      }
      
      // Căutăm cartea folosind Google Books API
      const response = await axios.get<GoogleBooksResponse>(`https://www.googleapis.com/books/v1/volumes?q=isbn:${cleanedISBN}`);
      
      if (response.data.totalItems === 0 || !response.data.items || response.data.items.length === 0) {
        return null; // Nu s-a găsit nicio carte cu acest ISBN
      }
      
      const bookData = response.data.items[0].volumeInfo;
      
      // Extragem datele relevante
      return {
        title: bookData.title,
        author: bookData.authors ? bookData.authors.join(', ') : undefined,
        description: bookData.description,
        publishedYear: bookData.publishedDate ? bookData.publishedDate.substring(0, 4) : undefined,
        category: bookData.categories ? bookData.categories[0] : undefined,
        isbn: cleanedISBN,
        coverImageUrl: bookData.imageLinks ? bookData.imageLinks.thumbnail : undefined,
        publisher: bookData.publisher,
        language: bookData.language,
        pageCount: bookData.pageCount
      };
    } catch (error) {
      console.error('Eroare la căutarea cărții după ISBN:', error);
      return null;
    }
  },
  
  /**
   * Caută o carte după titlu și autor folosind Google Books API
   * @param title Titlul cărții
   * @param author Autorul cărții (opțional)
   */
  searchByTitleAndAuthor: async (title: string, author?: string): Promise<BookMetadata | null> => {
    try {
      if (!title || title.trim() === '') {
        throw new Error('Titlul nu poate fi gol');
      }
      
      // Construim query-ul pentru căutare
      let query = `intitle:${encodeURIComponent(title.trim())}`;
      if (author && author.trim() !== '') {
        query += `+inauthor:${encodeURIComponent(author.trim())}`;
      }
      
      // Căutăm cartea folosind Google Books API
      const response = await axios.get<GoogleBooksResponse>(`https://www.googleapis.com/books/v1/volumes?q=${query}`);
      
      if (response.data.totalItems === 0 || !response.data.items || response.data.items.length === 0) {
        return null; // Nu s-a găsit nicio carte
      }
      
      const bookData = response.data.items[0].volumeInfo;
      
      // Extragem datele relevante
      return {
        title: bookData.title,
        author: bookData.authors ? bookData.authors.join(', ') : undefined,
        description: bookData.description,
        publishedYear: bookData.publishedDate ? bookData.publishedDate.substring(0, 4) : undefined,
        category: bookData.categories ? bookData.categories[0] : undefined,
        isbn: bookData.industryIdentifiers ? 
          bookData.industryIdentifiers.find((id) => id.type === 'ISBN_13')?.identifier || 
          bookData.industryIdentifiers.find((id) => id.type === 'ISBN_10')?.identifier : 
          undefined,
        coverImageUrl: bookData.imageLinks ? bookData.imageLinks.thumbnail : undefined,
        publisher: bookData.publisher,
        language: bookData.language,
        pageCount: bookData.pageCount
      };
    } catch (error) {
      console.error('Eroare la căutarea cărții după titlu și autor:', error);
      return null;
    }
  },
  
  /**
   * Descarcă o imagine de copertă de la URL-ul specificat
   * @param imageUrl URL-ul imaginii de copertă
   * @returns Fișierul imagine ca Blob sau null în caz de eroare
   */
  downloadCoverImage: async (imageUrl: string): Promise<Blob | null> => {
    try {
      if (!imageUrl || imageUrl.trim() === '') {
        throw new Error('URL-ul imaginii nu poate fi gol');
      }
      
      const response = await axios.get(imageUrl, { 
        responseType: 'blob' 
      });
      
      return response.data as Blob;
    } catch (error) {
      console.error('Eroare la descărcarea imaginii de copertă:', error);
      return null;
    }
  },
  
  /**
   * Creează un fișier din Blob și un nume de fișier dat
   * @param blob Blob-ul pentru imagine
   * @param fileName Numele fișierului (opțional)
   */
  createFileFromBlob: (blob: Blob, fileName: string = 'cover.jpg'): File => {
    return new File([blob], fileName, { type: blob.type });
  }
};

export default BookMetadataService; 